import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Plane, ArrowLeft, HelpCircle, ChevronDown, ChevronUp,
  Clock, Users, Shield, MapPin, CreditCard, Luggage,
  Globe, AlertTriangle, CheckCircle
} from 'lucide-react';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import AppLogo from '@/components/AppLogo';

const faqCategories = [
  {
    id: 'general',
    title: 'General Questions',
    icon: HelpCircle,
    questions: [
      {
        q: 'What is Airport Busyness Predictor?',
        a: `Airport Busyness Predictor is a free tool that helps travelers know how crowded their airport will be at a specific date and time. We analyze real-time flight data to predict crowd levels and recommend the optimal arrival time for your flight. Our goal is to help you avoid unnecessary waiting at the airport while ensuring you don't miss your flight.`
      },
      {
        q: 'How does the prediction work?',
        a: `Our predictions are based on multiple factors:
        
• **Flight Volume**: We count all departures and arrivals scheduled within a 2-hour window of your flight time.
• **Aircraft Size**: Larger planes (Boeing 777, Airbus A380) carry more passengers than regional jets, so they contribute more to crowd levels.
• **Time of Day**: Peak hours (early morning business travel, evening departures) typically see higher volumes.
• **Terminal Analysis**: When you provide a flight number, we can narrow predictions to your specific terminal.

We use FlightAware's AeroAPI for real-time flight schedules, ensuring accuracy for flights within 48 hours.`
      },
      {
        q: 'Is this service free?',
        a: `Yes! Airport Busyness Predictor is completely free to use. We believe everyone deserves to have a stress-free travel experience, regardless of whether they're a frequent flyer or occasional traveler. The site is supported by advertising.`
      },
      {
        q: 'Which airports do you support?',
        a: `We support 331+ airports worldwide, including:
        
• **128 US airports**: All major hubs and regional airports
• **203 international airports**: Major airports across Europe, Asia, South America, Africa, and Oceania

Our coverage includes all the world's busiest airports like ATL, LAX, ORD, DFW, JFK, LHR, DXB, CDG, and HND.`
      },
      {
        q: 'How accurate are your predictions?',
        a: `Accuracy depends on data availability:

• **Within 48 hours**: We use live flight data from FlightAware, providing high confidence predictions with actual scheduled flights.
• **3-7 days out**: We use a combination of confirmed schedules and historical patterns. Accuracy is good but may not capture last-minute schedule changes.
• **Beyond 7 days**: We provide estimates based on typical patterns for that day of week and time. These are helpful for planning but should be rechecked closer to your travel date.

We display a "Confidence" indicator with each prediction so you know how reliable it is.`
      }
    ]
  },
  {
    id: 'arrival-times',
    title: 'Arrival Time Recommendations',
    icon: Clock,
    questions: [
      {
        q: 'How early should I arrive at the airport?',
        a: `Our recommendations vary based on crowd levels and your specific situation:

**Domestic Flights (Carry-On Only)**:
• Low Traffic: 75-90 minutes before departure
• Medium Traffic: 90-105 minutes before departure  
• High Traffic: 105-120 minutes before departure
• Extreme Traffic: 135-150 minutes before departure

**Domestic Flights (Checking Bags)**: Add 15-30 minutes to the above times.

**International Flights**: Add 45-60 additional minutes for customs, immigration, and longer security procedures.

**With TSA PreCheck/CLEAR**: You can subtract 10-15 minutes from our recommendations, but we still advise arriving with buffer time.`
      },
      {
        q: 'What if I have TSA PreCheck or CLEAR?',
        a: `TSA PreCheck and CLEAR can significantly reduce your security wait time:

• **TSA PreCheck**: Average wait is 5-10 minutes vs. 20-30+ minutes in standard lines. You keep shoes, belt, and light jacket on, and laptops stay in your bag.
• **CLEAR**: Uses biometric screening to skip the document check line entirely. Combined with PreCheck, you can breeze through security.

When you toggle "TSA PreCheck / CLEAR" in our tool, we subtract 10-15 minutes from our recommended arrival time. However, we still recommend building in buffer time for unexpected delays.`
      },
      {
        q: 'How do international flights affect arrival times?',
        a: `International flights require extra time for several reasons:

• **Passport Control**: Document verification adds 5-15 minutes
• **Enhanced Security**: International terminals often have additional screening
• **Longer Walks**: International terminals tend to be larger with longer distances to gates
• **Check-in Requirements**: Many international flights require in-person check-in or document verification
• **Customs Forms**: Some countries require pre-departure customs declaration

We recommend arriving 2.5-3 hours early for international flights during low traffic, and up to 4 hours during extreme traffic periods.`
      },
      {
        q: 'Why do carry-on and checked bag times differ?',
        a: `When you're checking bags, you need extra time for:

• **Bag Drop**: Even with online check-in, you need to visit the counter or kiosk to tag and drop your bags (10-30 minutes depending on lines)
• **Bag Processing**: Airlines have cutoff times for checked bags, typically 45-60 minutes before departure for domestic and 60-90 minutes for international
• **Counter Queues**: Peak times can mean long lines at the check-in counter

If you're traveling carry-on only, you can skip directly to security, saving significant time.`
      }
    ]
  },
  {
    id: 'security',
    title: 'Airport Security',
    icon: Shield,
    questions: [
      {
        q: 'What is TSA PreCheck and how do I get it?',
        a: `TSA PreCheck is a trusted traveler program that allows expedited security screening:

**Benefits**:
• Keep shoes, belt, and light jacket on
• Leave laptop and liquids in your bag
• Use dedicated, shorter security lines
• Available at 200+ US airports

**How to Apply**:
1. Complete online application at tsa.gov ($78 for 5 years)
2. Schedule an in-person appointment at an enrollment center
3. Provide fingerprints and identity documents
4. Receive your Known Traveler Number (KTN) within 3-5 days

Many travel credit cards reimburse the TSA PreCheck fee, making it essentially free for cardholders.`
      },
      {
        q: 'What is CLEAR and is it worth it?',
        a: `CLEAR is a private security program that uses biometric identification:

**How It Works**:
• Enroll your fingerprints and iris scan
• At the airport, use CLEAR lanes to bypass the document check line
• CLEAR agents verify your identity biometrically
• You then proceed to the X-ray screening (PreCheck line if you have it)

**Cost**: $189/year (often discounted to $149 with promotions)

**Is It Worth It?**:
• For frequent travelers (10+ trips/year), yes—the time savings add up
• Works best when combined with TSA PreCheck
• Available at 50+ airports, primarily major hubs
• Some premium credit cards offer CLEAR credits or memberships`
      },
      {
        q: 'What are the busiest times for airport security?',
        a: `Security wait times vary throughout the day:

**Busiest Times**:
• 5:00-8:00 AM: Business travelers and early departures
• 11:00 AM-1:00 PM: Mid-day rush
• 4:00-7:00 PM: Evening departures and business returns

**Quietest Times**:
• 9:00-11:00 AM: After morning rush
• 2:00-4:00 PM: Mid-afternoon lull
• After 8:00 PM: Evening wind-down

**Busiest Days**: Monday, Friday, and Sunday typically see the highest volumes.

**Pro Tip**: Our tool factors in time of day when calculating crowd levels, giving you a more accurate picture of expected wait times.`
      },
      {
        q: 'What items are not allowed through security?',
        a: `TSA has strict rules about what can go in carry-on bags:

**Prohibited in Carry-On**:
• Liquids over 3.4 oz (100ml)
• Sharp objects (knives, scissors over 4 inches)
• Sporting goods (baseball bats, golf clubs)
• Guns and ammunition
• Tools over 7 inches
• Self-defense items (pepper spray, brass knuckles)

**Must Be Declared**:
• Medications in liquid form over 3.4 oz (allowed with declaration)
• Breast milk, formula, and baby food (exempt from 3-1-1 rule)

**Tips for Faster Screening**:
• Pack liquids in a clear quart-sized bag
• Remove laptops and large electronics
• Wear slip-on shoes and minimal jewelry
• Don't wrap gifts (TSA may need to open them)`
      }
    ]
  },
  {
    id: 'travel-tips',
    title: 'Travel Tips & Tricks',
    icon: Luggage,
    questions: [
      {
        q: 'What are the best days to fly to avoid crowds?',
        a: `Based on historical flight data, here are the best and worst days to fly:

**Best Days (Quietest)**:
1. **Tuesday**: Consistently the quietest day for air travel
2. **Wednesday**: Second-best for avoiding crowds
3. **Saturday mid-day**: Surprisingly calm between morning and evening rushes

**Worst Days (Busiest)**:
1. **Sunday**: Weekend return travel peaks
2. **Friday**: Business travel + weekend departures
3. **Monday**: Business travel at its highest

**Times to Avoid**:
• The day before Thanksgiving (busiest travel day of the year)
• December 22-23 (pre-Christmas rush)
• January 2-3 (post-holiday returns)
• Spring Break weeks (varies by region)`
      },
      {
        q: 'Should I book morning or evening flights?',
        a: `Each has advantages:

**Morning Flights (Before 9 AM)**:
✅ Highest on-time rates (planes are already at the gate)
✅ Fewer cascading delays from earlier flights
✅ Typically quieter security lines
✅ More rebooking options if something goes wrong
❌ Requires early wake-up and potentially pre-dawn travel to airport

**Evening Flights (After 6 PM)**:
✅ No early wake-up required
✅ Full day at your origin or destination
✅ Sometimes cheaper fares
❌ Higher chance of delays (accumulated throughout the day)
❌ If cancelled, fewer rebooking options
❌ Busier security during 4-7 PM rush

**Our Recommendation**: For important trips, book the first flight of the day. For flexibility, mid-morning (9-11 AM) offers a good balance.`
      },
      {
        q: 'How can I make the most of a long layover?',
        a: `Long layovers don't have to be boring:

**Airport Lounges** (see our lounge guide):
• Pay-per-use access ($40-65)
• Credit card memberships (Priority Pass, Centurion)
• Amenities: food, drinks, WiFi, showers, quiet space

**Airport Activities**:
• Many airports have art installations, museums, and gardens (SFO, MSP, SIN)
• Yoga rooms and fitness centers (DFW, SFO, ORD)
• Spas and massage services (most major hubs)
• Observation decks for plane spotting

**Leave the Airport**:
• Some airports are close to attractions (PHX to downtown, SFO to BART)
• Ensure you have enough time (recommend 5+ hour layover)
• Consider customs/re-security if international

**Work Productively**:
• Many lounges have conference rooms
• Quiet gates in off-peak terminals
• Airport hotels for day-use rates`
      },
      {
        q: 'What should I do if my flight is delayed or cancelled?',
        a: `Here's your action plan:

**Immediate Steps**:
1. Check the airline app for rebooking options (often faster than waiting in line)
2. Call the airline while waiting in the customer service line
3. Be polite but persistent—agents have more flexibility than you might think

**Know Your Rights**:
• **Cancelled Flight**: Entitled to full refund OR rebooking on next available flight
• **Significant Delay**: May be entitled to meal vouchers and hotel (depends on cause)
• **EU Flights**: EU261 regulation provides compensation for delays over 3 hours
• **Bumped from Flight**: Entitled to compensation up to $1,550 depending on delay

**Pro Tips**:
• Check other airlines with the same route
• Consider nearby airports
• Tweet at the airline (social media teams are often responsive)
• Use your credit card travel insurance if applicable

**Prevention**: Book the first flight of the day and allow longer connections during winter/storm season.`
      }
    ]
  },
  {
    id: 'specific-airports',
    title: 'Airport-Specific Questions',
    icon: MapPin,
    questions: [
      {
        q: 'Which US airports have the longest security wait times?',
        a: `Based on TSA data and traveler reports, these airports typically have longer waits:

**Longest Waits**:
1. **ATL (Atlanta)**: The world's busiest airport; South checkpoint is usually faster
2. **ORD (Chicago O'Hare)**: Massive hub; Terminal 1 PreCheck line recommended
3. **LAX (Los Angeles)**: Terminal 1 (Southwest) and TBIT (International) are slowest
4. **DFW (Dallas/Fort Worth)**: Terminals A and C (American hubs) during morning rush
5. **JFK (New York JFK)**: Terminal 4 evening international rush

**Fastest Major Airports**:
1. **SLC (Salt Lake City)**: New terminal with efficient design
2. **MSP (Minneapolis)**: Well-organized with good signage
3. **DTW (Detroit)**: McNamara terminal is efficient despite size
4. **SEA (Seattle)**: Multiple checkpoint options reduce congestion`
      },
      {
        q: 'How do I navigate airports with multiple terminals?',
        a: `Large airports require extra planning:

**Know Before You Go**:
• Check which terminal your airline uses (airline websites or our airport guides)
• Some airlines use multiple terminals (United at ORD: T1 and T2)
• International vs. domestic terminals are often separate

**Inter-Terminal Transportation**:
• **ATL**: Free underground Plane Train connects all concourses
• **DFW**: Free Skylink train (airside) connects all terminals
• **ORD**: Free ATS train connects domestic terminals; separate trip to T5 (international)
• **LAX**: Free shuttle or walk between some terminals; rideshare pickup at LAX-it

**Time Allowances**:
• Same terminal connection: 45-60 minutes minimum
• Different terminal (airside train): 60-90 minutes
• Different terminal (landside, re-security): 90-120 minutes
• Different airport (same city): 3+ hours`
      },
      {
        q: 'What are the best airports for food and amenities?',
        a: `These airports excel at passenger experience:

**Best Food**:
1. **SFO (San Francisco)**: Napa Farms Market, Cat Cora's Kitchen, local cuisine
2. **JFK (New York)**: Shake Shack, TWA Hotel, Deep Blue Sushi
3. **ORD (Chicago)**: Tortas Frontera, Garrett Popcorn, local favorites
4. **ATL (Atlanta)**: One Flew South, Paschal's, Southern cuisine
5. **SEA (Seattle)**: Beecher's Mac & Cheese, local seafood

**Best Amenities**:
• **SFO**: Free museum exhibitions, aquarium, yoga room
• **SIN (Singapore Changi)**: Butterfly garden, rooftop pool, movie theater
• **ICN (Seoul Incheon)**: Korean culture museum, spa, ice skating rink
• **DEN (Denver)**: Outdoor plaza, art installations, Westin hotel

**Best for Productivity**:
• **ATL**: Multiple lounges, business centers
• **DFW**: Minute Suites (nap pods), yoga studio
• **SFO**: SFO Library, quiet areas in International Terminal`
      }
    ]
  },
  {
    id: 'credit-cards-lounges',
    title: 'Credit Cards & Airport Lounges',
    icon: CreditCard,
    questions: [
      {
        q: 'What are the best credit cards for airport lounge access?',
        a: `Here are the top cards for lounge access:

**Premium Cards with Owned Lounges**:
• **Amex Platinum ($695/year)**: Centurion Lounges (best food/drinks), Delta Sky Clubs when flying Delta, Priority Pass
• **Chase Sapphire Reserve ($550/year)**: Priority Pass, new Chase Sapphire Lounges
• **Capital One Venture X ($395/year)**: Capital One Lounges, Priority Pass—best value in this category

**Priority Pass Cards**:
• **Amex Platinum**: Priority Pass Select (guests included)
• **Chase Sapphire Reserve**: Priority Pass Select (guests included)
• **Capital One Venture X**: Priority Pass (unlimited guests!)
• **Citi Prestige ($495/year)**: Priority Pass with guests

**Best Value**:
The **Capital One Venture X** at $395/year offers the best lounge access value, with Priority Pass, Capital One Lounges, and $300 annual travel credit that effectively reduces the fee to $95.`
      },
      {
        q: 'How does Priority Pass work?',
        a: `Priority Pass is a lounge network with 1,400+ locations worldwide:

**How to Get It**:
• Included with premium travel credit cards (Amex Platinum, Chase Sapphire Reserve, etc.)
• Or purchase directly: $99/year (+ $32/visit) to $469/year (unlimited visits)

**How to Use It**:
1. Download the Priority Pass app
2. Find participating lounges at your airport
3. Present your membership card or digital pass at the lounge entrance
4. Some cards allow guests; others charge $32/guest

**What's Included**:
• Food and beverages (varies by lounge)
• WiFi and workspaces
• Comfortable seating
• Some lounges: showers, sleeping pods, spa services

**Limitations**:
• Some popular lounges restrict Priority Pass during peak hours
• Restaurant credits (at participating locations) often capped at $28-30
• Guest policies vary by card issuer`
      },
      {
        q: 'What are Centurion Lounges and how do I access them?',
        a: `Centurion Lounges are American Express's premium airport lounges:

**Access Requirements**:
• American Express Platinum Card (personal or business)
• American Express Centurion Card (invitation only)
• Must be flying that day (any airline)

**What Makes Them Special**:
• **Food**: Chef-curated menus, often by local celebrity chefs
• **Drinks**: Premium wines, craft cocktails (not just well drinks)
• **Space**: Modern design, quieter atmosphere than most lounges
• **Amenities**: Shower suites, spa services (at select locations), family rooms

**Locations** (13 US lounges):
ATL, CLT, DEN, DFW, HOU, JFK (2), LAS, LAX, MIA, PHL, PHX, SEA, SFO

**Guest Policy** (as of 2023):
• Platinum cardholders: 2 free guests OR immediate family
• $50 per additional guest
• Children under 2 free

**Tips**:
• Visit during off-peak hours (mid-morning, mid-afternoon) to avoid crowds
• Some locations have better food than others (DFW and LAX are favorites)
• Spa reservations fill up—book early`
      },
      {
        q: 'Which credit cards offer TSA PreCheck or Global Entry credits?',
        a: `Many travel cards reimburse the application fee:

**Cards with TSA PreCheck/Global Entry Credit ($100 value)**:
• Amex Platinum
• Chase Sapphire Reserve
• Capital One Venture X
• Citi Prestige
• Amex Gold (PreCheck only, $85)
• Chase United Club Infinite
• Delta Reserve Amex

**How It Works**:
1. Apply for Global Entry ($100) or TSA PreCheck ($78) on the official CBP/TSA website
2. Pay with your eligible credit card
3. The credit appears as a statement credit within 1-2 billing cycles
4. Credits typically renew every 4-5 years (matching the program renewal)

**Pro Tip**: Apply for Global Entry instead of TSA PreCheck—it includes PreCheck benefits plus expedited customs/immigration for international travel, same credit amount.`
      },
      {
        q: 'Are airport lounges worth visiting without a credit card membership?',
        a: `Yes! There are several ways to access lounges without a membership card:

**Pay-Per-Visit Options**:
• **Priority Pass** restaurants: $28-30 credit at participating airport restaurants
• **Day passes**: Many lounges sell day passes ($40-65)
• **LoungeBuddy app**: Book lounge access at various airports

**Airline Status**:
• Elite status (earned or via credit card) often includes lounge access
• First/business class tickets typically include lounge access
• Same-day upgrades may include lounge privileges

**When It's Worth Paying**:
✅ Long layover (3+ hours)
✅ Delayed flight (saves on expensive airport food)
✅ Need to work (reliable WiFi, quiet space)
✅ Early morning flight (breakfast included)
✅ Flying with kids (family rooms, better snacks)

**When to Skip**:
❌ Short layover (under 1 hour)
❌ Airport has great terminal food options
❌ You're already well-fed and rested`
      }
    ]
  }
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full py-4 flex items-start justify-between gap-4 text-left hover:bg-slate-50/50 px-4 -mx-4 transition-colors"
        aria-expanded={isOpen}
      >
        <h3 className="font-medium text-slate-800 pr-4">{question}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        )}
      </button>
      <div className={isOpen ? 'block' : 'hidden'}>
        <div 
          className="pb-4 px-4 -mx-4 text-slate-600 text-sm leading-relaxed prose prose-sm max-w-none prose-headings:text-slate-800 prose-strong:text-slate-700 prose-li:text-slate-600"
          dangerouslySetInnerHTML={{ 
            __html: answer
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\n\n/g, '</p><p>')
              .replace(/\n• /g, '</p><ul><li>')
              .replace(/• /g, '</li><li>')
              .replace(/<\/li><li>([^<]*?)(?=<\/p>|$)/g, '</li></ul><p>$1')
              .replace(/✅/g, '<span class="text-green-600">✅</span>')
              .replace(/❌/g, '<span class="text-red-500">❌</span>')
          }}
        />
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('general');

  const toggleItem = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="Airport FAQs — Crowd Levels, TSA Lines, Arrival Times"
        description="Answers to the most-asked airport questions: how early to arrive, TSA PreCheck vs CLEAR, busiest times, terminals, kids and pets. Updated for 2026 travel."
        path="/faq"
        keywords="airport FAQ, how early to arrive at airport, TSA wait times, TSA PreCheck vs CLEAR, airport security questions, airport kids, airport pets"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'FAQ', url: '/faq' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqCategories.flatMap((c) =>
              c.questions.map((q) => ({
                '@type': 'Question',
                name: q.q,
                acceptedAnswer: { '@type': 'Answer', text: q.a },
              }))
            ),
          },
        ]}
      />
      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <h1 className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900 leading-[1.15]">
                How Busy Is My Airport
              </h1>
            </div>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </motion.div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-10 sm:py-14">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Frequently Asked Questions</h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Everything you need to know about airport travel, security, and making the most of your journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Category Navigation */}
          <div className="mb-8 overflow-x-auto -mx-4 px-4">
            <div className="flex gap-2 min-w-max pb-2">
              {faqCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeCategory === category.id
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {category.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* FAQ Categories */}
          {faqCategories.map((category) => (
            <div
              key={category.id}
              className={activeCategory === category.id ? '' : 'hidden'}
            >
              {(
                <Card className="border-slate-200 bg-white shadow-sm mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <category.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-slate-800">{category.title}</h2>
                        <p className="text-sm text-slate-500">{category.questions.length} questions</p>
                      </div>
                    </div>

                    <div className="space-y-0">
                      {category.questions.map((item, idx) => (
                        <FAQItem
                          key={idx}
                          question={item.q}
                          answer={item.a}
                          isOpen={openItems[`${category.id}-${idx}`]}
                          onToggle={() => toggleItem(category.id, idx)}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}

          {/* Quick Links */}
          <Card className="border-slate-200 bg-white shadow-sm mt-8">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Still have questions?</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <Link 
                  to="/"
                  className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Plane className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-800">Check Busyness</p>
                    <p className="text-sm text-slate-500">Try our predictor</p>
                  </div>
                </Link>
                <Link 
                  to="/airports"
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <MapPin className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="font-medium text-slate-800">Airport Guides</p>
                    <p className="text-sm text-slate-500">Terminal & tips</p>
                  </div>
                </Link>
                <Link 
                  to="/blog"
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <HelpCircle className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="font-medium text-slate-800">Travel Blog</p>
                    <p className="text-sm text-slate-500">Tips & guides</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <div className="text-center mt-8 p-6 bg-blue-50 rounded-xl">
            <p className="text-slate-600 mb-2">Can't find what you're looking for?</p>
            <a 
              href="mailto:jamursch@gmail.com"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Contact us at jamursch@gmail.com
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/airports" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Airport Guides</Link>
            <Link to="/blog" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/about" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/privacy" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
